import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { parseVitalProgramConfigurationValue } from "@tellescope/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/** The Select value for the program at `index`, kept non-empty so Radix accepts it as an item value. */
const getProgramOptionValue = (index: number) => `program-${index}`;

/**
 * The program an option value refers to, or undefined if it matches none. Resolving by value rather
 * than by the displayed label matters for untitled programs, whose label is a shared fallback string
 * that belongs to no single program.
 */
export const getProgramForOptionValue = (
  programs: ReturnType<typeof parseVitalProgramConfigurationValue>["programs"] | undefined,
  optionValue: string
) => programs?.find((_, index) => getProgramOptionValue(index) === optionValue);

export const useVitalProgramsAndThresholds = () => {
  const { t } = useTranslation("vitals");

  const { data: vitalPrograms } = useTellescopeInfiniteQuery({
    model: "configurations",
    method: "getSome",
    request: {
      limit: 500,
      filter: {
        type: "Vital Programs",
      },
    },
    select: (data) => {
      return data.pages.flat().reduce<{
        programs: ReturnType<typeof parseVitalProgramConfigurationValue>["programs"];
        configurationIds: Set<string>;
      }>(
        (acc, program) => {
          const { programs } = parseVitalProgramConfigurationValue(program.value);
          acc.programs.push(...programs);
          programs.forEach((program) => program.configurationIds.forEach((id) => acc.configurationIds.add(id)));
          return acc;
        },
        { programs: [], configurationIds: new Set<string>() }
      );
    },
  });

  // Only the configurations the programs actually reference. Without the id filter this pulls every
  // org-level vital configuration, and `enabled` never gates anything — the Set is always truthy
  // once the programs resolve, empty or not.
  const programConfigurationIds = useMemo(
    () => [...(vitalPrograms?.configurationIds ?? [])],
    [vitalPrograms?.configurationIds]
  );

  const { data: thresholdsFromPrograms = [] } = useTellescopeInfiniteQuery({
    model: "vital_configurations",
    method: "getSome",
    request: {
      limit: 500,
      filter: {
        enduserId: {
          _exists: false,
        },
        id: {
          _in: programConfigurationIds,
        },
      },
    },
    enabled: programConfigurationIds.length > 0,
    select: (data) => data.pages.flat(),
  });

  const programOptions = useMemo(
    () =>
      vitalPrograms?.programs.map((program, index) => ({
        value: getProgramOptionValue(index),
        label: program.title || t("Unknown Program"),
      })) ?? [],
    [vitalPrograms, t]
  );

  const thresholdOptions = useMemo(
    () =>
      thresholdsFromPrograms.map((threshold) => ({
        value: threshold.id,
        label: threshold.title,
      })),
    [thresholdsFromPrograms]
  );

  const defaultProgramConfigIds = vitalPrograms?.programs[0]?.configurationIds;

  return {
    vitalPrograms,
    thresholdsFromPrograms,
    programOptions,
    thresholdOptions,
    defaultProgramConfigIds,
  };
};
